import React, { useState } from 'react'
import PropTypes from 'prop-types'

import {
  Archive as ArchiveIcon,
  Grid as GridIcon,
  File as FileIcon,
  HelpCircle as HelpCircleIcon,
  Layers as LayersIcon,
  Package as PackageIcon,
  Play as PlayIcon,
  Shield as ShieldIcon,
  ShieldOff as ShieldOffIcon,
  ThumbsUp as ThumbsUpIcon,
} from 'react-feather'

import { Grid, Tooltip, Button } from '@material-ui/core'
import { DataUsage as DataUsageIcon } from '@material-ui/icons'

import {
  Container,
  ContentHeader,
  LoadingFeedback,
  DriveTour,
  SVG,
} from 'components'

import {
  DashboardActivities,
  DashboardAutomaticActivities,
  DashboardComplaint,
  DashboardConsent,
  DashboardCookie,
  DashboardCrossActivities,
  DashboardDiagnostic,
  DashboardMain,
  DashboardPrivacyPolicy,
  DashboardProcess,
  DashboardSupplier,
  DashboardTabs,
  DashboardTicket,
  DashboardTraining,
} from './components'

import usePermissions from 'hooks/usePermissions'
import useBrowserReachability from 'hooks/useBrowserReachability'

import helpers from 'helpers'
import constants from 'constants/index'
import permissions from 'constants/permissions'
import * as service from 'service'

const DashboardGraphs = ({
  complaintStats,
  consentStats,
  cookieStats,
  fragilityStats,
  gapStats,
  generalStats,
  privacyPolicyStats,
  processStats,
  questionControlStats,
  questionnaires,
  supplierStats,
  ticketStats,
  trainingStats,
  loading = false,
}) => {
  const [openTour, setOpenTour] = useState(false)
  const [currentTab, setCurrentTab] = useState(
    constants.myLgpd.DASHBOARD_MAIN_TAB,
  )

  const { permitted, some } = usePermissions()

  const isCookiesPermitted = permitted(permissions.COOKIES.MANAGE)
  const isCookiesAuthenticated = service.cookies.auth.isAuthenticated()
  const isActivitiesPermitted = permitted(permissions.KANBAN_TASKS.STATS)

  // Enquanto a verificação está em andamento a aba continua visível: só some quando
  // o bloqueio é confirmado. Assim o caminho feliz não pisca.
  const { isReachable: isCookiesReachable } = useBrowserReachability(
    constants.externalServices.COOKIES_KEY,
  )

  const isCookiesAvailable =
    isCookiesAuthenticated && isCookiesPermitted && isCookiesReachable

  const tabs = [
    {
      value: constants.myLgpd.DASHBOARD_MAIN_TAB,
      label: 'TODOS',
      icon: <DataUsageIcon fontSize="inherit" />,
    },
    {
      value: constants.myLgpd.DASHBOARD_PROCESS_TAB,
      label: 'PROCESSOS',
      icon: <LayersIcon size={16} />,
    },
    {
      value: constants.myLgpd.DASHBOARD_DIAGNOSTIC_TAB,
      label: 'DIAGNÓSTICO',
      icon: <FileIcon size={16} />,
    },
    {
      value: constants.myLgpd.DASHBOARD_SUPPLIER_TAB,
      label: 'FORNECEDORES',
      icon: <PackageIcon size={16} />,
    },
    {
      value: constants.myLgpd.DASHBOARD_TRAINING_TAB,
      label: 'TREINAMENTOS',
      icon: <PlayIcon size={16} />,
    },
    {
      value: constants.myLgpd.DASHBOARD_CONSENT_TAB,
      label: 'CONSENTIMENTOS',
      icon: <ThumbsUpIcon size={16} />,
    },
    ...(isCookiesAvailable
      ? [
          {
            value: constants.myLgpd.DASHBOARD_COOKIES_TAB,
            label: 'COOKIES',
            icon: <SVG.CookieIcon size={16} />,
          },
        ]
      : []),
    {
      value: constants.myLgpd.DASHBOARD_DOCUMENTS_TAB,
      label: 'DOCUMENTOS',
      icon: <ArchiveIcon size={16} />,
    },
    {
      value: constants.myLgpd.DASHBOARD_TICKET_TAB,
      label: 'TITULARES',
      icon: <ShieldIcon size={16} />,
    },
    {
      value: constants.myLgpd.DASHBOARD_COMPLAINT_TAB,
      label: 'CANAL DE DENÚNCIAS',
      icon: <ShieldOffIcon size={16} />,
    },
    ...(isActivitiesPermitted
      ? [
          {
            value: constants.myLgpd.DASHBOARD_ACTIVITIES_TAB,
            label: 'ATIVIDADES',
            icon: <GridIcon size={16} />,
          },
        ]
      : []),
  ]

  const handleTabsChange = (_, value) => {
    const { label } = tabs.find((tab) => tab.value === value)
    helpers.mixpanel.track('Dashboard', label, {
      action: `${label.toLowerCase()}-dashboard`,
    })

    setCurrentTab(value)
  }

  const handleOpenTour = () => {
    const where = 'Dashboard'
    const title = 'Tour'

    const params = {
      action: 'tour-dashboard',
    }

    helpers.mixpanel.track(where, title, params)
    setOpenTour(true)
  }

  const validationSteps = constants.myLgpd.DASHBOARD_DRIVER_STEPS_OBJECT

  return (
    <Container
      maxWidth={false}
      spacingXl={15}
      spacingLg={10}
      spacingMd={5}
      spacingSm={5}
    >
      <ContentHeader title="Indicadores">
        <Tooltip title="Iniciar o tour guiado">
          <Button
            id={constants.myLgpd.DASHBOARD_DRIVER_STEP_1}
            variant="contained"
            color="primary"
            startIcon={<HelpCircleIcon size={20} />}
            onClick={handleOpenTour}
            fullWidth
          >
            Tutorial
          </Button>
        </Tooltip>
      </ContentHeader>

      <LoadingFeedback open={loading} />
      <Grid container spacing={3}>
        <DashboardTabs
          tabs={tabs}
          handleTabsChange={handleTabsChange}
          currentTab={currentTab}
        />
        {currentTab === constants.myLgpd.DASHBOARD_MAIN_TAB && (
          <DashboardMain
            complaintStats={complaintStats}
            consentStats={consentStats}
            cookieStats={cookieStats}
            fragilityStats={fragilityStats}
            gapStats={gapStats}
            generalStats={generalStats}
            isCookiesAvailable={isCookiesAvailable}
            privacyPolicyStats={privacyPolicyStats}
            processStats={processStats}
            questionControlStats={questionControlStats}
            questionnaires={questionnaires}
            supplierStats={supplierStats}
            ticketStats={ticketStats}
            trainingStats={trainingStats}
          />
        )}
        {currentTab === constants.myLgpd.DASHBOARD_PROCESS_TAB && (
          <DashboardProcess processStats={processStats} loading={loading} />
        )}
        {currentTab === constants.myLgpd.DASHBOARD_DIAGNOSTIC_TAB && (
          <DashboardDiagnostic
            gapStats={gapStats}
            questionnaires={questionnaires}
            fragilityStats={fragilityStats}
            questionControlStats={questionControlStats}
          />
        )}
        {currentTab === constants.myLgpd.DASHBOARD_SUPPLIER_TAB && (
          <DashboardSupplier supplierStats={supplierStats} loading={loading} />
        )}
        {currentTab === constants.myLgpd.DASHBOARD_TRAINING_TAB && (
          <DashboardTraining trainingStats={trainingStats} />
        )}
        {currentTab === constants.myLgpd.DASHBOARD_CONSENT_TAB && (
          <DashboardConsent consentStats={consentStats} />
        )}
        {currentTab === constants.myLgpd.DASHBOARD_COOKIES_TAB &&
          isCookiesAvailable && <DashboardCookie cookieStats={cookieStats} />}
        {currentTab === constants.myLgpd.DASHBOARD_DOCUMENTS_TAB && (
          <DashboardPrivacyPolicy privacyPolicyStats={privacyPolicyStats} />
        )}
        {currentTab === constants.myLgpd.DASHBOARD_TICKET_TAB && (
          <DashboardTicket ticketStats={ticketStats} />
        )}
        {currentTab === constants.myLgpd.DASHBOARD_COMPLAINT_TAB && (
          <DashboardComplaint complaintStats={complaintStats} />
        )}
        {currentTab === constants.myLgpd.DASHBOARD_ACTIVITIES_TAB && (
          <>
            <DashboardActivities />
            <DashboardCrossActivities />
            <DashboardAutomaticActivities />
          </>
        )}
      </Grid>
      <DriveTour
        stepsMatrix={validationSteps}
        open={openTour}
        setOpen={setOpenTour}
      />
    </Container>
  )
}

DashboardGraphs.propTypes = {
  complaintStats: PropTypes.object.isRequired,
  consentStats: PropTypes.object.isRequired,
  cookieStats: PropTypes.array.isRequired,
  fragilityStats: PropTypes.object.isRequired,
  gapStats: PropTypes.object.isRequired,
  generalStats: PropTypes.object.isRequired,
  privacyPolicyStats: PropTypes.object.isRequired,
  processStats: PropTypes.object.isRequired,
  questionControlStats: PropTypes.object.isRequired,
  questionnaires: PropTypes.array.isRequired,
  supplierStats: PropTypes.object.isRequired,
  ticketStats: PropTypes.object.isRequired,
  trainingStats: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
}

export default DashboardGraphs
